import axios from "axios";
import { proxyUrl } from "../config";
import { SubscriptionType } from "./SubscriptionService";
import { SavingsPlanType } from "./SavingsPlanService";
import { setupInterceptorsTo } from "./Interceptors";
setupInterceptorsTo(axios);

interface IUsersSubscription
{
    id?: number,
    userId?: number,
    subscriptionId?: number,
    subscription?: SubscriptionType,
    savingsPlans?: SavingsPlanType[],
    createdAt?: string,
    startDate?: string
    endDate?: string
}

export type TSubscriptionStatusDTO = {
    status: boolean;
    message: string;
}

export type TUsersSubscriptionStatusDTO = {
    userId: number;
    subscriptionId: number;
    startDate: Date;
    endDate: Date;
    subscriptionStatus: TSubscriptionStatusDTO
}

// Tarifa tal y como la devuelve GET subscriptionOptions/{usersSubscriptionId}. limit null = sin límite.
export type TSubscriptionOptionDTO =
{
    id: number;
    name: string;
    type: string;
    period: string;
    price: number;
    limit: number | null;
}

// Opción de mejora: chargePrice son los euros a cobrar antes de descontar Elements.
export type TUpgradeOptionDTO = TSubscriptionOptionDTO &
{
    chargePrice: number;
}

// "prorated": experience→experience, se cobran los días restantes y no se amplía la validez.
// "promotionToExperience": se cobra la tarifa íntegra y la validez pasa a ser desde hoy hasta newEndDate.
// "monthlyToAnnual": desde IronIA Mes a Mes a una anual superior; igual que promotionToExperience
// (tarifa íntegra y validez nueva desde hoy hasta newEndDate).
export type TUpgradeMode = "prorated" | "promotionToExperience" | "monthlyToAnnual";

export type TUpgradeOptionsDTO =
{
    allowed: boolean;
    mode: TUpgradeMode | null;
    notAllowedReason: string | null;
    options: TUpgradeOptionDTO[];
    recommendedId: number | null;
    newStartDate: string | null;
    newEndDate: string | null;
}

export type TRenewalOptionsDTO =
{
    allowed: boolean;
    notAllowedReason: string | null;
    options: TSubscriptionOptionDTO[];
    requiredSubscriptionId: number | null;
    currentFits: boolean;
    message: string | null;
}

export type TSubscriptionOptionsDTO =
{
    usersSubscriptionId: number;
    currentSubscription: TSubscriptionOptionDTO;
    startDate: string | null;
    endDate: string | null;
    remainingDays: number;
    isExpired: boolean;
    portfolioValue: number;
    isBonus: boolean;
    // Precio de un Element Tierra con el que cobra el backend. No viene si el backend aún no lo publica.
    elementEarthPrice?: number;
    upgrade: TUpgradeOptionsDTO;
    renewal: TRenewalOptionsDTO;
}

export type UsersSubscriptionType = IUsersSubscription;

class UsersSubscriptionService
{
    public createUsersSubscription = async(usersSubscription: UsersSubscriptionType): Promise<UsersSubscriptionType> =>
    {
        usersSubscription.createdAt = new Date().toUTCString();
        let url: string = `${proxyUrl}/usersSubscriptions`;
        const req = await axios.post(url, usersSubscription);
        return req.data
    }
    public getPendingPromotioned = async(userId: number, savingsPlanId: number): Promise<UsersSubscriptionType> =>
    {
        let url: string = `${proxyUrl}/usersSubscriptions/${userId}/${savingsPlanId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getLimitAmount = async(userSubscriptionId: number): Promise<number> =>
    {
        let url: string = `${proxyUrl}/getLimitAmount/${userSubscriptionId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getUserSubscriptionsByType = async(userId: number, type: string): Promise<UsersSubscriptionType[]> =>
    {
        let url: string = `${proxyUrl}/userSubscriptionsByType/${userId}/${type}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getOutdatedPriceBySubscriptionId = async (userSubscriptionId: number): Promise<any> =>
    {
        let url: string = `${proxyUrl}/GetOutdatedPriceBySubscriptionId/${userSubscriptionId}`;
        const req = await axios.get(url);
        return req.data;
    }
    // Opciones de mejora y renovación de una suscripción del usuario. El backend es la única fuente de las
    // reglas de tarifa (qué opciones caben, cuál es la requerida y cuánto se cobra). 401/403/404 si no es suya.
    public getSubscriptionOptions = async (usersSubscriptionId: number): Promise<TSubscriptionOptionsDTO> =>
    {
        let url: string = `${proxyUrl}/subscriptionOptions/${usersSubscriptionId}`;
        const req = await axios.get(url);
        return req.data;
    }
    public getUsersSubscriptionStatusByUserIdAndSubscriptionId = async ({ userId, subscriptionId } : { userId: number, subscriptionId: number }): Promise<TUsersSubscriptionStatusDTO> => {
        let url: string = `${proxyUrl}/usersSubscriptions/getUserSubscriptionStatusByIdAndSubscriptionId/${userId}/${subscriptionId}`;
        const req = await axios.get(url);
        return req.data;
    }
}

const UsersSubscription = new UsersSubscriptionService();
export default UsersSubscription;